import {
	Label,
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@exopengithub/exokit";
import { Input } from "molecules/input/Input";
import { Select } from "molecules/select/Select";
import { useTranslation } from "react-i18next";
import { type UseFormReturn, Controller } from "react-hook-form";

import { FormSection } from "src/features/planning/components/formSection/FormSection";

import findTenantIdGif from "./find-tenant-id.gif";
import {
	useGetVismanetBranchQuery,
	useListSubAccountSegmentIdsQuery,
} from "src/common/service/fusionHub/integrations/vismanetApi";

interface PureProps {
	form: UseFormReturn<any>;
	tenantId: string;
	branch?: { name: string | null } | undefined;
	dimensions?: Array<{ id: string; name: string }> | undefined;
}

// Pure presentational component: receives form, tenantId, branch + dimensions as props.
// Views and stories render this directly with fixture data. For live fetch use
// VismaNetDimensionSectionWithData below.
export const VismaNetDimensionSection = ({
	form,
	tenantId,
	branch,
	dimensions,
}: PureProps) => {
	const { t } = useTranslation();

	return (
		<>
			<FormSection
				title={t("Tenant")}
				description={t(
					"Enter the tenant id of the VismaNet company that is being onboarded. It can be found in the Visma App Store.",
				)}
			>
				<div className="mb-4">
					<Label htmlFor="tenant-id">
						{t("Tenant id")}{" "}
						<Tooltip>
							<TooltipTrigger>
								<div className="text-purple-900">
									{t("(Show me where to find it)")}
								</div>
							</TooltipTrigger>
							<TooltipContent>
								<img src={findTenantIdGif} alt="find-tenant-id" width="860" />
							</TooltipContent>
						</Tooltip>
					</Label>
					<Input
						{...form.register("tenantId", {
							required: t("This field is required"),
						})}
						placeholder={t("Enter tenant id")}
						aria-invalid={form.formState.errors.tenantId !== undefined}
						ariaLabel="Tenant id input"
						outsideRight={tenantId ? (branch?.name ?? undefined) : undefined}
					/>
				</div>
			</FormSection>
			<FormSection
				title={t("Dimensions")}
				description={t(
					"Map the VismaNet dimensions to the corresponding Exopen dimensions, cost center and project. The mapping is optional but certain features require the mapping to be set up.",
				)}
			>
				<div className="mb-4 w-min">
					<Label>{t("Cost center dimension")}</Label>
					<Controller
						control={form.control}
						name="costCenterId"
						render={({ field: { onChange, ...rhfField }, fieldState }) => {
							return (
								<Select
									className="mb-2"
									withFilter
									clearable
									ariaLabel="Cost center selection"
									placeholder={t("Select a VismaNet dimension")}
									{...rhfField}
									onChange={(value) => {
										onChange(value);
									}}
									options={
										dimensions
											? dimensions.map((dimension) => {
													return {
														value: dimension.id,
														label: dimension.name,
													};
												})
											: []
									}
									disabled={!dimensions}
									ariaInvalid={fieldState.error !== undefined}
								/>
							);
						}}
					/>
				</div>
				<div className="mb-4 w-min">
					<Label>{t("Project dimension")}</Label>
					<Controller
						control={form.control}
						name="projectId"
						render={({ field: { onChange, ...rhfField }, fieldState }) => {
							return (
								<Select
									className="mb-2"
									withFilter
									clearable
									ariaLabel="Project selection"
									placeholder={t("Select a VismaNet dimension")}
									{...rhfField}
									onChange={(value) => {
										onChange(value);
									}}
									options={
										dimensions
											? dimensions.map((dimension) => {
													return {
														value: dimension.id,
														label: dimension.name,
													};
												})
											: []
									}
									disabled={!dimensions}
									ariaInvalid={fieldState.error !== undefined}
								/>
							);
						}}
					/>
				</div>
			</FormSection>
		</>
	);
};

interface ConnectedProps {
	form: UseFormReturn<any>;
}

// Connected wrapper: fetches branch + dimensions, passes to pure component.
// Use in container contexts where hooks are allowed.
export const VismaNetDimensionSectionWithData = ({ form }: ConnectedProps) => {
	const tenantId = form.watch("tenantId");

	const { data: branch } = useGetVismanetBranchQuery(
		{ tenantId },
		{
			skip:
				!tenantId ||
				!/^[a-f\d]{8}(-[a-f\d]{4}){3}-[a-f\d]{12}$/i.test(tenantId),
		},
	);

	const { data: dimensions } = useListSubAccountSegmentIdsQuery(
		{ tenantId },
		{
			skip:
				!tenantId ||
				!/^[a-f\d]{8}(-[a-f\d]{4}){3}-[a-f\d]{12}$/i.test(tenantId),
		},
	);

	return (
		<VismaNetDimensionSection
			form={form}
			tenantId={tenantId}
			branch={branch}
			dimensions={dimensions}
		/>
	);
};
